// components/Clinician/PatientRegistration.jsx
// Redesigned Patient registration form matching mockup design exactly
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, MenuItem,
  Dialog, DialogTitle, DialogContent, Alert, Snackbar,
  CircularProgress, IconButton, Checkbox, FormControlLabel
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { patientService } from '../../services/ris';
import {
  getAllCountries,
  getStatesForCountry,
  getCountryLabel,
  getStateLabel,
  resolveCountryCode,
  resolveStateCode,
} from '../../utils/locationData';

const initialFormState = {
  mrn: '',
  firstName: '',
  middleName: '',
  lastName: '',
  sex: '',
  birthDate: '',
  telephone: '',
  email: '',
  address: '',
  state: '',
  country: 'NG'
};

const ID_TYPES = [
  { value: 'mrn', label: 'MRN Number' },
  { value: 'patient_no', label: 'Patient Number' },
  { value: 'hospital_no', label: 'Hospital Number' }
];

const GENDERS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' }
];

const COUNTRIES = getAllCountries();

// NDPA 2023 (s.26) consent notice — must match CLAUDE/backend CONSENT_NOTICE_VERSION.
const CONSENT_NOTICE_VERSION = '1.0';
const CONSENT_NOTICE_TEXT =
  'I consent to DycoVue and the facility processing my personal and health data for the purpose of ' +
  'medical imaging, diagnosis, reporting and related care, in line with the Nigeria Data Protection Act 2023. ' +
  'I understand I may withdraw consent at any time, subject to records that must be retained by law.';

export default function PatientRegistration({ open, onClose, onSuccess, onCreateOrder, editPatient, systemSource }) {
  const [form, setForm] = useState(initialFormState);
  const [idType, setIdType] = useState('mrn');
  const [errors, setErrors] = useState({});
  const [consentGiven, setConsentGiven] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [registeredPatient, setRegisteredPatient] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useEffect(() => {
    if (editPatient) {
      let streetAddress = '';
      let stateVal = '';
      let countryVal = '';
      if (editPatient.address) {
        const parts = editPatient.address.split(',').map(p => p.trim());
        if (parts.length >= 3) {
          countryVal = parts[parts.length - 1];
          stateVal = parts[parts.length - 2];
          streetAddress = parts.slice(0, -2).join(', ');
        } else if (parts.length === 2) {
          stateVal = parts[1];
          streetAddress = parts[0];
        } else {
          streetAddress = parts[0] || '';
        }
      }

      let bDate = '';
      if (editPatient.birthDate) {
        try {
          const d = new Date(editPatient.birthDate);
          if (!isNaN(d.getTime())) {
            bDate = d.toISOString().split('T')[0];
          } else {
            bDate = editPatient.birthDate.substring(0, 10);
          }
        } catch (e) {
          bDate = editPatient.birthDate.substring(0, 10);
        }
      }

      setForm({
        mrn: editPatient.mrn || '',
        firstName: editPatient.firstName || '',
        middleName: editPatient.middleName || '',
        lastName: editPatient.lastName || '',
        sex: editPatient.sex || '',
        birthDate: bDate,
        telephone: editPatient.telephone || '',
        email: editPatient.email || '',
        address: streetAddress,
        country: resolveCountryCode(countryVal),
        state: resolveStateCode(resolveCountryCode(countryVal), stateVal),
      });
    } else {
      setForm(initialFormState);
    }
    setErrors({});
    setRegisteredPatient(null);
  }, [editPatient, open]);

  const formatDateOfBirth = (dateString) => {
    if (!dateString) return '-';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const monthIndex = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${day} ${months[monthIndex]} ${year}`;
      }
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatGender = (sex) => {
    if (sex === 'M') return 'Male';
    if (sex === 'F') return 'Female';
    if (sex === 'O') return 'Other';
    return sex || '-';
  };

  const handleRegisterAnother = () => {
    setShowSuccessModal(false);
    if (!editPatient) {
      setRegisteredPatient(null);
      setForm(initialFormState);
      setErrors({});
    } else {
      if (onClose) onClose();
    }
  };

  const handleCreateOrderClick = () => {
    setShowSuccessModal(false);
    const pt = registeredPatient || {
      ...form,
      patientName: `${form.firstName} ${form.lastName}`.trim(),
      patientId: form.patientId || form.mrn,
      mrn: form.mrn || form.patientId,
    };
    if (onCreateOrder) {
      onCreateOrder(pt);
    } else if (onSuccess) {
      onSuccess(pt);
      if (onClose) onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
      setForm((prev) => ({ ...prev, country: value, state: '' }));
      if (errors.country || errors.state) {
        setErrors((prev) => ({ ...prev, country: '', state: '' }));
      }
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const stateOptions = getStatesForCountry(form.country);
  const hasStateOptions = stateOptions.length > 0;

  const validate = () => {
    const errs = {};
    const selectedIdLabel = ID_TYPES.find(t => t.value === idType)?.label || 'Patient ID';
    if (!form.mrn.trim()) errs.mrn = `${selectedIdLabel} is required`;
    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';
    if (!form.sex) errs.sex = 'Gender is required';
    if (!form.birthDate) errs.birthDate = 'Date of birth is required';
    if (!form.telephone?.trim()) errs.telephone = 'Phone number is required';
    if (!form.address?.trim()) errs.address = 'Address is required';
    if (!form.country) errs.country = 'Country is required';
    if (!form.state?.trim()) errs.state = hasStateOptions ? 'State is required' : 'State / region is required';
    if (!editPatient && !consentGiven) errs.consent = 'Patient consent is required (NDPA 2023 s.26)';

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Invalid email format';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = { ...form };
      
      // Concatenate Address, State, and Country (human-readable labels)
      const stateLabel = getStateLabel(form.country, form.state) || form.state;
      const countryLabel = getCountryLabel(form.country) || form.country;
      const addressParts = [form.address.trim(), stateLabel, countryLabel].filter(Boolean);
      payload.address = addressParts.join(', ');
      
      // Remove local UI fields before sending
      delete payload.state;
      delete payload.country;

      // NDPA 2023 s.26 consent capture (registration only — never edited here)
      if (!editPatient) {
        payload.consent = {
          consentGiven: true,
          consentMethod: 'checkbox_in_clinic',
          consentVersion: CONSENT_NOTICE_VERSION,
          consentText: CONSENT_NOTICE_TEXT
        };
      }

      // Clean empty strings
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = undefined;
      });

      // Tag patient with system origin (RIS / LIS / REFERRED)
      if (systemSource && !editPatient) {
        payload.systemSource = systemSource;
      }

      const res = editPatient
        ? await patientService.update(editPatient._id, payload)
        : await patientService.register(payload);
      
      const savedPatient = res.data?.patient || res.data || res;
      setRegisteredPatient(savedPatient);
      setShowSuccessModal(true);
    } catch (err) {
      const msg = err.response?.data?.error || (editPatient ? 'Failed to update patient' : 'Failed to register patient');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm(initialFormState);
    setErrors({});
    if (registeredPatient && onSuccess) {
      onSuccess(registeredPatient);
    } else if (onClose) {
      onClose();
    }
  };

  const handleDialogClose = () => {
    if (registeredPatient && onSuccess) {
      onSuccess(registeredPatient);
    } else if (onClose) {
      onClose();
    }
  };

  // Satoshi Typography Style Helpers
  const satoshiBold = {
    fontFamily: '"Satoshi", sans-serif',
    fontWeight: 700,
  };

  const labelStyle = {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: isDark ? '#CBD5E1' : '#1E293B',
    mb: 0.5,
    fontFamily: '"Satoshi", sans-serif',
    display: 'flex',
    alignItems: 'center',
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: isDark ? '#121212' : '#FFFFFF',
      height: '32px',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#E2E8F0'}`,
      transition: 'border-color 0.2s',
      '& fieldset': {
        border: 'none',
      },
      '&:hover': {
        borderColor: isDark ? 'rgba(255,255,255,0.25)' : '#CBD5E1',
      },
      '&.Mui-focused': {
        borderColor: '#0D9488',
        borderWidth: '1.5px',
      },
    },
    '& .MuiInputBase-input': {
      py: 0.5,
      px: 1.25,
      fontSize: '0.8rem',
      color: isDark ? '#F1F5F9' : '#0F172A',
      fontWeight: 500,
      fontFamily: '"Satoshi", sans-serif',
      '&::placeholder': {
        color: isDark ? '#64748B' : '#94A3B8',
        opacity: 1,
      }
    },
    '& .MuiFormHelperText-root': {
      fontFamily: '"Satoshi", sans-serif',
      mx: 0,
      mt: 0.25,
      fontSize: '0.7rem',
    }
  };

  const selectSx = {
    ...textFieldSx,
    '& .MuiSelect-select': {
      py: 0,
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.8rem',
      color: form.sex || idType || form.state || form.country ? (isDark ? '#F1F5F9' : '#0F172A') : (isDark ? '#64748B' : '#94A3B8'),
      fontWeight: 500,
      fontFamily: '"Satoshi", sans-serif',
    }
  };

  const cardStyle = {
    p: 2,
    mb: 1.75,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
    boxShadow: 'none',
    borderRadius: '10px',
    bgcolor: isDark ? '#1A1A1A' : '#FFFFFF',
  };

  const cardHeaderBox = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    mb: 1.5,
  };

  const iconCircleStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '8px',
    color: '#0D9488',
    bgcolor: isDark ? 'rgba(13, 148, 136, 0.15)' : '#F0FDFA',
  };

  // Main UI form contents matching the mockup exactly
  const formContent = (
    <Box component="form" onSubmit={handleSubmit} sx={{ fontFamily: '"Satoshi", sans-serif' }}>
      
      {/* Patient Information Card */}
      <Paper sx={cardStyle}>
        <Box sx={cardHeaderBox}>
          <Box sx={iconCircleStyle}>
            {/* Person Outline SVG */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </Box>
          <Typography variant="h6" sx={{ ...satoshiBold, color: isDark ? '#F1F5F9' : '#0F172A', fontSize: '0.9rem' }}>
            Patient Information
          </Typography>
        </Box>

        <Grid container spacing={1.5}>
          {/* ID Type */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              ID Type <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              select
              name="idType"
              value={idType}
              onChange={(e) => setIdType(e.target.value)}
              size="small"
              sx={selectSx}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) return <span style={{ color: '#94A3B8' }}>Select ID Type</span>;
                  return ID_TYPES.find(t => t.value === selected)?.label;
                }
              }}
            >
              <MenuItem disabled value="" sx={{ fontFamily: '"Satoshi", sans-serif' }}>Select ID Type</MenuItem>
              {ID_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value} sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.9rem' }}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* ID Number / MRN */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              ID Number <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="mrn"
              placeholder="Enter ID number"
              value={form.mrn}
              onChange={handleChange}
              error={!!errors.mrn}
              helperText={errors.mrn}
              size="small"
              sx={textFieldSx}
            />
          </Grid>

          {/* First Name */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              First Name <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="firstName"
              placeholder="Enter first name"
              value={form.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
              size="small"
              sx={textFieldSx}
            />
          </Grid>

          {/* Last Name */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              Last Name <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="lastName"
              placeholder="Enter last name"
              value={form.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
              size="small"
              sx={textFieldSx}
            />
          </Grid>

          {/* Date of Birth */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              Date of Birth <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="date"
              name="birthDate"
              value={form.birthDate}
              onChange={handleChange}
              error={!!errors.birthDate}
              helperText={errors.birthDate}
              size="small"
              sx={textFieldSx}
              InputLabelProps={{ shrink: false }}
            />
          </Grid>

          {/* Gender */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              Gender <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              select
              name="sex"
              value={form.sex}
              onChange={handleChange}
              error={!!errors.sex}
              helperText={errors.sex}
              size="small"
              sx={selectSx}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) return <span style={{ color: '#94A3B8' }}>Select gender</span>;
                  return GENDERS.find(g => g.value === selected)?.label;
                }
              }}
            >
              <MenuItem disabled value="" sx={{ fontFamily: '"Satoshi", sans-serif' }}>Select gender</MenuItem>
              {GENDERS.map((g) => (
                <MenuItem key={g.value} value={g.value} sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.9rem' }}>
                  {g.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              Phone Number <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="telephone"
              placeholder="Enter phone number"
              value={form.telephone}
              onChange={handleChange}
              error={!!errors.telephone}
              helperText={errors.telephone}
              size="small"
              sx={textFieldSx}
            />
          </Grid>

          {/* Email (Optional) */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              Email (Optional)
            </Typography>
            <TextField
              fullWidth
              name="email"
              placeholder="Enter email address"
              type="email"
              value={form.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              size="small"
              sx={textFieldSx}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Address Card */}
      <Paper sx={cardStyle}>
        <Box sx={cardHeaderBox}>
          <Box sx={iconCircleStyle}>
            {/* Map Pin Outline SVG */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </Box>
          <Typography variant="h6" sx={{ ...satoshiBold, color: isDark ? '#F1F5F9' : '#0F172A', fontSize: '0.9rem' }}>
            Address
          </Typography>
        </Box>

        <Grid container spacing={1.5}>
          {/* Street Address */}
          <Grid item xs={12} md={6}>
            <Typography sx={labelStyle}>
              Address <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="address"
              placeholder="Enter full address"
              value={form.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
              size="small"
              sx={textFieldSx}
            />
          </Grid>

          {/* Country first — state depends on country */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              Country <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            <TextField
              fullWidth
              select
              name="country"
              value={form.country}
              onChange={handleChange}
              error={!!errors.country}
              helperText={errors.country}
              size="small"
              sx={selectSx}
              SelectProps={{
                displayEmpty: true,
                MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
                renderValue: (selected) => {
                  if (!selected) return <span style={{ color: '#94A3B8' }}>Select country</span>;
                  return COUNTRIES.find(c => c.value === selected)?.label || selected;
                }
              }}
            >
              <MenuItem disabled value="" sx={{ fontFamily: '"Satoshi", sans-serif' }}>Select country</MenuItem>
              {COUNTRIES.map((c) => (
                <MenuItem key={c.value} value={c.value} sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.9rem' }}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* State / Province */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={labelStyle}>
              {hasStateOptions ? 'State / Province' : 'State / Region'}{' '}
              <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
            </Typography>
            {hasStateOptions ? (
              <TextField
                fullWidth
                select
                name="state"
                value={form.state}
                onChange={handleChange}
                error={!!errors.state}
                helperText={errors.state}
                size="small"
                sx={selectSx}
                disabled={!form.country}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) return <span style={{ color: '#94A3B8' }}>Select state</span>;
                    return stateOptions.find(s => s.value === selected)?.label || selected;
                  }
                }}
              >
                <MenuItem disabled value="" sx={{ fontFamily: '"Satoshi", sans-serif' }}>Select state</MenuItem>
                {stateOptions.map((s) => (
                  <MenuItem key={s.value} value={s.value} sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.9rem' }}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                name="state"
                placeholder={form.country ? 'Enter state or region' : 'Select country first'}
                value={form.state}
                onChange={handleChange}
                error={!!errors.state}
                helperText={errors.state || (form.country ? 'Type your state or region' : '')}
                size="small"
                sx={textFieldSx}
                disabled={!form.country}
              />
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* NDPA 2023 s.26 — Consent capture (registration only) */}
      {!editPatient && (
        <Paper
          sx={{
            ...cardStyle,
            borderColor: errors.consent
              ? '#EF4444'
              : (isDark ? 'rgba(13,148,136,0.35)' : '#CCFBF1'),
            bgcolor: isDark ? 'rgba(13,148,136,0.08)' : '#F0FDFA',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: '6px', color: '#0D9488',
                bgcolor: isDark ? 'rgba(13,148,136,0.15)' : '#FFFFFF',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#CCFBF1'}`,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </Box>
            <Typography sx={{ ...satoshiBold, color: isDark ? '#F1F5F9' : '#0F172A', fontSize: '0.85rem' }}>
              Data Protection Consent <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={consentGiven}
                onChange={(e) => {
                  setConsentGiven(e.target.checked);
                  if (errors.consent) setErrors(prev => ({ ...prev, consent: '' }));
                }}
                size="small"
                sx={{
                  color: '#0D9488',
                  '&.Mui-checked': { color: '#0D9488' },
                  py: 0,
                }}
              />
            }
            sx={{ m: 0, alignItems: 'flex-start' }}
            label={
              <Typography sx={{ fontSize: '0.74rem', color: isDark ? '#CBD5E1' : '#334155', lineHeight: 1.5, fontFamily: '"Satoshi", sans-serif' }}>
                {CONSENT_NOTICE_TEXT}
                <Box component="span" sx={{ display: 'block', mt: 0.4, fontSize: '0.68rem', color: isDark ? '#64748B' : '#94A3B8' }}>
                  Notice version {CONSENT_NOTICE_VERSION}. The patient/guardian has read and agreed to this notice.
                </Box>
              </Typography>
            }
          />

          {errors.consent && (
            <Typography sx={{ color: '#EF4444', fontSize: '0.7rem', mt: 0.5, fontFamily: '"Satoshi", sans-serif', fontWeight: 600 }}>
              {errors.consent}
            </Typography>
          )}
        </Paper>
      )}

      {/* Fields marked with * are required banner */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          px: 1.5,
          borderRadius: '8px',
          bgcolor: isDark ? '#1A1A1A' : '#F8FAFC',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
          mb: 1.5,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#64748B' : '#64748B'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600, fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif' }}>
          Fields marked with <span style={{ color: '#EF4444' }}>*</span> are required.
        </Typography>
      </Box>

      {/* Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  // Standalone panel layout matching mockup exactly
  const pageHeader = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1.5,
        flexWrap: 'wrap',
        gap: 1.5,
        fontFamily: '"Satoshi", sans-serif',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
        py: 1,
        mx: -0.5,
        px: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* User-Plus Teal Circle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            borderRadius: '8px',
            color: '#0D9488',
            bgcolor: isDark ? 'rgba(13, 148, 136, 0.15)' : '#F0FDFA',
            border: '1px solid #CCFBF1',
          }}
        >
          {/* User Plus SVG */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <line x1="20" y1="8" x2="20" y2="14" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
        </Box>
        <Box>
          <Typography variant="h5" sx={{ ...satoshiBold, color: isDark ? '#F1F5F9' : '#0F172A', fontSize: '1.05rem', letterSpacing: '-0.02em', mb: 0.25 }}>
            {editPatient ? 'Edit Patient Details' : 'Register New Patient'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500, fontSize: '0.75rem' }}>
            {editPatient ? 'Update patient demographic information.' : 'Enter patient demographic information.'}
          </Typography>
        </Box>
      </Box>

      {/* Action Buttons Top Right */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={loading}
          startIcon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          }
          sx={{
            textTransform: 'none',
            borderRadius: '6px',
            px: 2,
            py: 0.5,
            height: 32,
            fontWeight: 700,
            fontFamily: '"Satoshi", sans-serif',
            borderColor: '#CBD5E1',
            color: isDark ? '#CBD5E1' : '#1E293B',
            bgcolor: isDark ? '#2D2D2D' : '#FFFFFF',
            fontSize: '0.8rem',
            boxShadow: 'none',
            '&:hover': {
              borderColor: '#94A3B8',
              backgroundColor: isDark ? '#3D3D3D' : '#F8FAFC',
              boxShadow: 'none',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
            )
          }
          sx={{
            textTransform: 'none',
            borderRadius: '6px',
            px: 2.25,
            py: 0.5,
            height: 32,
            fontWeight: 700,
            fontFamily: '"Satoshi", sans-serif',
            backgroundColor: '#00665C',
            color: '#FFFFFF',
            fontSize: '0.8rem',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#004D47',
              boxShadow: 'none',
            }
          }}
        >
          {editPatient ? 'Save Changes' : 'Save Patient'}
        </Button>
      </Box>
    </Box>
  );

  const successModal = (
    <Dialog
      open={showSuccessModal}
      onClose={() => setShowSuccessModal(false)}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '460px' },
          maxWidth: '480px !important',
          maxHeight: '85vh',
          borderRadius: '16px',
          p: 3,
          position: 'relative',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
          fontFamily: '"Satoshi", sans-serif',
          overflow: 'hidden'
        }
      }}
    >
      <IconButton
        onClick={() => {
          setShowSuccessModal(false);
          if (onClose) onClose();
        }}
        size="small"
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          zIndex: 10,
          color: isDark ? '#94A3B8' : '#64748B',
          '&:hover': {
            bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#F1F5F9'
          }
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <DialogContent sx={{ p: 0, textAlign: 'center' }}>
        {/* Confetti & Checkmark */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5, position: 'relative' }}>
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              bgcolor: '#ECFDF5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#10B981',
              border: '3px solid #F0FDF4',
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </Box>
          <Box sx={{ position: 'absolute', top: 6, left: '38%', width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981', opacity: 0.6 }} />
          <Box sx={{ position: 'absolute', top: 32, left: '34%', width: 7, height: 7, borderRadius: '50%', bgcolor: '#34D399', opacity: 0.4 }} />
          <Box sx={{ position: 'absolute', top: 12, right: '36%', width: 5, height: 5, borderRadius: '50%', bgcolor: '#059669', opacity: 0.5 }} />
          <Box sx={{ position: 'absolute', top: 36, right: '33%', width: 6, height: 6, borderRadius: '50%', bgcolor: '#6EE7B7', opacity: 0.6 }} />
        </Box>

        <Typography variant="h5" sx={{ ...satoshiBold, color: isDark ? '#F1F5F9' : '#0F172A', fontSize: '1.15rem', mb: 0.5 }}>
          {editPatient ? 'Patient Updated Successfully' : 'Patient Registered Successfully'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500, fontSize: '0.8rem', mb: 2 }}>
          {editPatient ? 'The patient record has been updated successfully.' : 'The patient record has been created successfully.'}
        </Typography>

        {/* Details list */}
        <Box
          sx={{
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
            borderRadius: '10px',
            p: 1.25,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            bgcolor: isDark ? '#121212' : '#F8FAFC',
            textAlign: 'left',
          }}
        >
          {/* Row 1: Patient Name */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.75, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9'}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: isDark ? '#94A3B8' : '#64748B', fontFamily: '"Satoshi", sans-serif' }}>
                Patient Name
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.825rem', fontWeight: 800, color: isDark ? '#F1F5F9' : '#0F172A', fontFamily: '"Satoshi", sans-serif' }}>
              {registeredPatient ? `${registeredPatient.firstName} ${registeredPatient.lastName}` : '-'}
            </Typography>
          </Box>

          {/* Row 2: Patient ID */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.75, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9'}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="4" />
                <line x1="8" y1="2" x2="8" y2="4" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: isDark ? '#94A3B8' : '#64748B', fontFamily: '"Satoshi", sans-serif' }}>
                Patient ID
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.825rem', fontWeight: 800, color: isDark ? '#F1F5F9' : '#0F172A', fontFamily: '"Satoshi", sans-serif' }}>
              {registeredPatient?.mrn || registeredPatient?.patientId || '-'}
            </Typography>
          </Box>

          {/* Row 3: Date of Birth */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.75, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9'}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: isDark ? '#94A3B8' : '#64748B', fontFamily: '"Satoshi", sans-serif' }}>
                Date of Birth
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.825rem', fontWeight: 800, color: isDark ? '#F1F5F9' : '#0F172A', fontFamily: '"Satoshi", sans-serif' }}>
              {registeredPatient ? formatDateOfBirth(registeredPatient.birthDate) : '-'}
            </Typography>
          </Box>

          {/* Row 4: Gender */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                <path d="M2 12h20" />
              </svg>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: isDark ? '#94A3B8' : '#64748B', fontFamily: '"Satoshi", sans-serif' }}>
                Gender
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.825rem', fontWeight: 800, color: isDark ? '#F1F5F9' : '#0F172A', fontFamily: '"Satoshi", sans-serif' }}>
              {registeredPatient ? formatGender(registeredPatient.sex) : '-'}
            </Typography>
          </Box>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
          <Button
            variant="outlined"
            onClick={handleRegisterAnother}
            fullWidth
            startIcon={
              editPatient ? (
                <CloseIcon sx={{ fontSize: 16 }} />
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              )
            }
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              height: '34px',
              fontWeight: 700,
              fontFamily: '"Satoshi", sans-serif',
              borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#CBD5E1',
              color: isDark ? '#CBD5E1' : '#1E293B',
              bgcolor: isDark ? '#2D2D2D' : '#FFFFFF',
              fontSize: '0.82rem',
              boxShadow: 'none',
              '&:hover': {
                borderColor: '#94A3B8',
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#F8FAFC',
                boxShadow: 'none',
              }
            }}
          >
            {editPatient ? 'Close' : 'Register Another'}
          </Button>
          <Button
            onClick={handleCreateOrderClick}
            variant="contained"
            fullWidth
            startIcon={
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
            }
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              height: '34px',
              fontWeight: 700,
              fontFamily: '"Satoshi", sans-serif',
              backgroundColor: '#00665C',
              color: '#FFFFFF',
              fontSize: '0.82rem',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#004D47',
                boxShadow: 'none',
              }
            }}
          >
            Create Order
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );

  // Dialog Wrapper if open prop is passed
  if (open !== undefined) {
    return (
      <>
        <Dialog
          open={open && !showSuccessModal}
          onClose={handleDialogClose}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '90vw', md: '840px' },
              maxWidth: '860px !important',
              maxHeight: '88vh',
              borderRadius: '14px',
              p: 2.5,
              position: 'relative',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.08)',
              backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
              fontFamily: '"Satoshi", sans-serif',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ p: 0, pb: 1.5, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}` }}>
            {pageHeader}
          </DialogTitle>
          <DialogContent sx={{ p: 0, pt: 1.5, overflowY: 'auto' }}>
            {formContent}
          </DialogContent>
        </Dialog>
        {successModal}
      </>
    );
  }

  // Otherwise render as standalone page
  return (
    <Box sx={{ p: 0 }}>
      {pageHeader}
      {formContent}
      {successModal}
    </Box>
  );
}
